import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useLocation } from 'react-router-dom';
import { Box, Grid, List, ListItem, ListSubheader, MenuItem, TextField } from '@mui/material';
import { ListItemLink } from 'components/mui/ListItemLink';
import CircularProgressWithLabel from 'components/mui/CircularProgressWithLabel';
import { isFProject } from 'config/project';
import { CarModel } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import useCarsFilter from 'modules/cars/hooks/useCarsFilter';
import { calcReadiness } from 'modules/cars/utils/common';
import ProblemMark from 'modules/cars/common/ProblemMark';
import auth from 'recoil/atoms/auth';


type CarMenuProps = {
    current: number,
}

const CarMenu = ({current}: CarMenuProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const location = useLocation();
    const {
        filter,
        changeFilter,
        cars,
        yardFilterOptions,
        voyageFilterOptions,
        companyFilterOptions,
    } = useCarsFilter();

    const nested = useMemo(() => {
            const sorted = cars
                .sort((a, b) =>
                    ((a.yard?.portId || 0) - (b.yard?.portId || 0)) ||
                    ((a.yard?.id || 0) - (b.yard?.id || 0)) ||
                    (a.company.id - b.company.id) ||
                    (b.id - a.id)
                );

            const nested: { [k: string]: { type: string, title?: string, car: CarModel }[] } = {};

            let lastCompany = my.type === CompanyType.Shipper ? my.company.id : 0;

            sorted.forEach(c => {
                const name = c.yard ? `${c.yard.port} ${c.yard.name}` : t('no_yard');

                if (!(name in nested)) {
                    nested[name] = [];
                }

                if (lastCompany !== c.company.id) {
                    nested[name].push({type: 'title', title: c.company.name, car: c});
                    lastCompany = c.company.id;
                }

                nested[name].push({type: 'car', car: c});
            })

            return nested;
        },
        // eslint-disable-next-line
        [cars, filter]);

    return (
        <Box sx={{
            height: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Grid container spacing={2} sx={{mb: 2}}>
                <Grid item xs={3}>
                    <TextField
                        select
                        id="yard"
                        name="yard"
                        label={t('yard')}
                        fullWidth
                        variant="outlined"
                        value={filter.yard === undefined ? '' : filter.yard}
                        onChange={e => changeFilter(e.target.name, e.target.value)}
                    >
                        {yardFilterOptions.map(({value, label}) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                </Grid>

                <Grid item xs={3}>
                    <TextField
                        select
                        id="voyage"
                        name="voyage"
                        label={t('voyage')}
                        fullWidth
                        variant="outlined"
                        value={filter.voyage === undefined ? '' : filter.voyage}
                        onChange={e => changeFilter(e.target.name, e.target.value)}
                    >
                        {voyageFilterOptions.map(({value, label}) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                </Grid>

                {companyFilterOptions.length > 2 &&
                    <Grid item xs={3}>
                        <TextField
                            select
                            id="shipper"
                            name="shipper"
                            label={t('shipper')}
                            fullWidth
                            variant="outlined"
                            value={filter.shipper === undefined ? '' : filter.shipper}
                            onChange={e => changeFilter(e.target.name, e.target.value)}
                        >
                            {companyFilterOptions.map(({value, label}) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                        </TextField>
                    </Grid>
                }

                <Grid item xs={3}>
                    <TextField
                        select
                        id="state"
                        name="state"
                        label={t('state')}
                        fullWidth
                        variant="outlined"
                        value={filter.state === undefined ? '' : filter.state}
                        onChange={e => changeFilter(e.target.name, e.target.value)}
                    >
                        <MenuItem value="">{t('any')}</MenuItem>
                        <MenuItem value="unknown">{t('unknown')}</MenuItem>
                        <MenuItem value="whole">{t('whole')}</MenuItem>
                        <MenuItem value="dis">{t('dismantled')}</MenuItem>
                        {!isFProject && <MenuItem value="dmg">{t('damaged')}</MenuItem>}
                    </TextField>
                </Grid>
            </Grid>

            <Box sx={{overflow: 'auto'}}>
                <List
                    aria-label="car menu"
                    sx={{
                        '& ul': {padding: 0},
                    }}
                    subheader={<li/>}
                >
                    {Object.entries(nested).map(([yard, items]) => (
                        <li key={yard}>
                            <ul>
                                <ListSubheader color="primary">{yard}</ListSubheader>
                                {items.map(item => (
                                    item.type === 'title' ?
                                        <ListItem
                                            key={`${item.title}${item.car.id}`}
                                            sx={{
                                                color: 'primary.light',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {item.title}
                                        </ListItem> :
                                        <ListItemLink
                                            key={item.car.id}
                                            to={`/car/${item.car.id}${location.search}`}
                                            sx={{
                                                bgcolor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderBottomColor: 'grey.300',
                                            }}
                                            primary={<>
                                                {item.car.model}, <Box component="span"
                                                                       whiteSpace="nowrap">
                                                {item.car.chassisNumber}
                                            </Box>
                                                {' '}
                                                <ProblemMark car={item.car}/>
                                            </>}
                                            selected={current === item.car.id}
                                            endAddon={
                                                <CircularProgressWithLabel value={calcReadiness(item.car)}/>
                                            }
                                        />
                                ))}
                            </ul>
                        </li>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

export default CarMenu;
